import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components following SongQ design system
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  '& .MuiTableHead-root': {
    backgroundColor: '#121212',
  },
  '& .MuiTableCell-root': {
    borderColor: '#333',
    color: '#fff',
  },
  '& .MuiTableCell-head': {
    backgroundColor: '#121212',
    fontWeight: 600,
    color: '#4CAF50',
  },
  '& .MuiTableRow-root': {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#2A2A2A',
    },
  },
}));

const CountChip = styled(Chip)(({ theme }) => ({
  margin: '2px',
  fontSize: '0.75rem',
  height: '20px',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const SongQuestionsSummaryTable = ({
  songSummaries = [],
  isLoading,
  onSongClick,
  searchTerm
}) => {
  // Helper function to format count breakdowns
  const formatCountBreakdown = (countMap, colorMap = {}) => {
    const entries = Array.from(countMap.entries());
    if (entries.length === 0) return <Typography variant="caption" sx={{ color: '#666' }}>None</Typography>;
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {entries.map(([key, count]) => (
          <CountChip
            key={key}
            label={`${key}(${count})`}
            size="small"
            sx={{
              backgroundColor: colorMap[key] || '#2196F3',
              color: '#fff',
              fontWeight: 500
            }}
          />
        ))}
      </Box>
    );
  };

  // Color mappings for different categories
  const difficultyColors = {
    'Easy': '#4CAF50',
    'Medium': '#FF9800',
    'Hard': '#F44336',
    'Very Hard': '#9C27B0',
    'Expert': '#E91E63'
  };

  const answerTypeColors = {
    'Multiple Choice': '#2196F3',
    'True/False': '#FF5722',
    'Single Choice': '#9C27B0',
    'Yes/No': '#607D8B'
  };

  const questionTypeColors = {
    'Text': '#4CAF50',
    'Image': '#FF9800',
    'Audio': '#9C27B0',
    'Video': '#F44336'
  };

  if (isLoading) {
    return (
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Song Name</TableCell>
              <TableCell>Question Count</TableCell>
              <TableCell>Difficulty Breakdown</TableCell>
              <TableCell>Answer Type Breakdown</TableCell>
              <TableCell>Question Type Breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <CircularProgress sx={{ color: '#4CAF50' }} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </StyledTableContainer>
    );
  }

  if (songSummaries.length === 0) {
    return (
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Song Name</TableCell>
              <TableCell>Question Count</TableCell>
              <TableCell>Difficulty Breakdown</TableCell>
              <TableCell>Answer Type Breakdown</TableCell>
              <TableCell>Question Type Breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" sx={{ color: '#ccc' }}>
                  {searchTerm ? 'No songs match your search' : 'No songs with questions found'}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Song Name</TableCell>
            <TableCell>Question Count</TableCell>
            <TableCell>Difficulty Breakdown</TableCell>
            <TableCell>Answer Type Breakdown</TableCell>
            <TableCell>Question Type Breakdown</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {songSummaries.map((song) => (
            <TableRow 
              key={`${song.songs_master_id}-${song.song_name}`}
              hover
              onClick={() => onSongClick(song)}
            >
              <TableCell>
                <Box>
                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                    {song.song_name}
                  </Typography>
                  {song.artist && (
                    <Typography variant="caption" sx={{ color: '#ccc' }}>
                      by {song.artist}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              
              <TableCell>
                <Chip
                  label={song.questionCount}
                  size="small"
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                />
              </TableCell>
              
              <TableCell>
                {formatCountBreakdown(song.difficulties, difficultyColors)}
              </TableCell>
              
              <TableCell>
                {formatCountBreakdown(song.answerTypes, answerTypeColors)}
              </TableCell>
              
              <TableCell>
                {formatCountBreakdown(song.questionTypes, questionTypeColors)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default SongQuestionsSummaryTable;
